import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { 
  FaTimes, FaPlus, FaCalendarCheck, FaInfoCircle,
  FaTrash, FaBoxOpen, FaClipboardList
} from 'react-icons/fa';
import '../index.css';

export default function Security() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [securityLogs, setSecurityLogs] = useState([]);
  const [lostAndFound, setLostAndFound] = useState([
    { id: 'LF-021', item: 'Black Wallet', description: 'Found in Main Hall', status: 'Found', location: 'Main Hall', date: new Date().toLocaleDateString() },
    { id: 'LF-022', item: 'Keys on blue lanyard', description: 'Found near Exit 2', status: 'Found', location: 'Exit 2', date: new Date().toLocaleDateString() },
    { id: 'LF-023', item: 'iPhone 15', description: 'Found in Lobby', status: 'Claimed', location: 'Lobby', date: new Date().toLocaleDateString() },
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Modal states
  const [showDateApprovalModal, setShowDateApprovalModal] = useState(false);
  const [showAccessAuthModal, setShowAccessAuthModal] = useState(false);
  const [showEventVerifyModal, setShowEventVerifyModal] = useState(false);
  const [showSecurityArrangementModal, setShowSecurityArrangementModal] = useState(false);
  const [showLostItemModal, setShowLostItemModal] = useState(false);
  const [showEventSecurityModal, setShowEventSecurityModal] = useState(false);
  const [selectedEventForSecurity, setSelectedEventForSecurity] = useState(null);
  
  // Form states
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dateApprovalForm, setDateApprovalForm] = useState({ eventId: '', dateVerified: true, remarks: '' });
  const [accessAuthForm, setAccessAuthForm] = useState({ eventId: '', authorizedPersons: '', accessLevel: 'Standard', remarks: '' });
  const [eventVerifyForm, setEventVerifyForm] = useState({ eventId: '', verified: false, details: '' });
  const [securityArrangementForm, setSecurityArrangementForm] = useState({ eventId: '', securityPersonnel: '', equipment: '', verified: false, remarks: '' });
  const [lostItemForm, setLostItemForm] = useState({ item: '', description: '', location: '', status: 'Found' });
  const [eventSecurityForm, setEventSecurityForm] = useState({ securityMembers: '', report: '' });
  
  // Filter and sort states
  const [statusFilter, setStatusFilter] = useState('All');
  const [approvalFilter, setApprovalFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  useEffect(() => {
    fetchEvents();
    fetchSecurityLogs();
  }, []);

  async function fetchEvents() {
    setLoading(true);
    try {
      const r = await api.get('/events');
      setEvents(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setError('Failed to load events');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function fetchSecurityLogs() {
    setLoading(true);
    try {
      const r = await api.get('/security');
      setSecurityLogs(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      console.error('Failed to load security logs:', e);
      setError('Failed to load security logs');
    } finally {
      setLoading(false);
    }
  }

  async function handleUpdateSecurityLogStatus(id, newStatus) {
    try {
      const response = await api.put(`/security/${id}`, { status: newStatus });
      // Update local state with server response to get updated timestamp
      if (response.data) {
        setSecurityLogs(securityLogs.map(log => 
          log.id === id ? response.data : log
        ));
      } else {
        // Fallback: just update status if server didn't return full object
        setSecurityLogs(securityLogs.map(log => 
          log.id === id ? { ...log, status: newStatus } : log
        ));
      }
    } catch (err) {
      alert('Failed to update status: ' + (err?.response?.data?.message || err?.message));
    }
  }

  async function handleUpdateApprovalStatus(id, newApprovalStatus) {
    try {
      const response = await api.put(`/security/${id}`, { approvalStatus: newApprovalStatus });
      // Update local state with server response to get updated timestamp
      if (response.data) {
        setSecurityLogs(securityLogs.map(log => 
          log.id === id ? response.data : log
        ));
      } else {
        // Fallback: just update approval status if server didn't return full object
        setSecurityLogs(securityLogs.map(log => 
          log.id === id ? { ...log, approval_status: newApprovalStatus } : log
        ));
      }
    } catch (err) {
      alert('Failed to update approval status: ' + (err?.response?.data?.message || err?.message));
    }
  }

  function handleViewSecurityLog(log) {
    // Show a modal or alert with full log details
    const details = `
ID: ${log.id}
Event: ${log.event_name || 'N/A'}${log.event_date ? ` (${new Date(log.event_date).toLocaleDateString()})` : ''}
Officer: ${log.officer_name || 'N/A'}${log.officer_email ? ` (${log.officer_email})` : ''}
Status: ${log.status || 'N/A'}
Report: ${log.report || 'N/A'}
Created: ${log.createdAt ? new Date(log.createdAt).toLocaleString() : 'N/A'}
Updated: ${log.updatedAt ? new Date(log.updatedAt).toLocaleString() : 'N/A'}
    `;
    alert(details);
  }

  async function handleDateApproval(e) {
    e.preventDefault();
    try {
      const approvalStatus = dateApprovalForm.dateVerified ? 'Approved' : 'Rejected';
      const report = `DATE_APPROVAL: ${approvalStatus} - ${dateApprovalForm.remarks}`;
      await api.post('/security', {
        eventId: dateApprovalForm.eventId,
        report: report,
        verificationType: 'Date Approval',
        approvalStatus: approvalStatus,
        dateApproved: dateApprovalForm.dateVerified,
        dateApprovalRemarks: dateApprovalForm.remarks
      });
      alert('Date approval submitted successfully');
      setShowDateApprovalModal(false);
      setDateApprovalForm({ eventId: '', dateVerified: true, remarks: '' });
      fetchSecurityLogs();
    } catch (err) {
      alert('Failed to submit: ' + (err?.response?.data?.message || err?.message));
    }
  }

  async function handleAccessAuthorization(e) {
    e.preventDefault();
    try {
      const report = `ACCESS_AUTH: Level=${accessAuthForm.accessLevel}, Persons=${accessAuthForm.authorizedPersons} - ${accessAuthForm.remarks}`;
      await api.post('/security', {
        eventId: accessAuthForm.eventId,
        report: report,
        verificationType: 'Access Authorization',
        approvalStatus: 'Approved',
        accessLevel: accessAuthForm.accessLevel,
        authorizedPersons: accessAuthForm.authorizedPersons,
        accessRemarks: accessAuthForm.remarks
      });
      alert('Access authorization submitted successfully');
      setShowAccessAuthModal(false);
      setAccessAuthForm({ eventId: '', authorizedPersons: '', accessLevel: 'Standard', remarks: '' });
      fetchSecurityLogs();
    } catch (err) {
      alert('Failed to submit: ' + (err?.response?.data?.message || err?.message));
    }
  }

  async function handleEventVerification(e) {
    e.preventDefault();
    try {
      const approvalStatus = eventVerifyForm.verified ? 'Approved' : 'Rejected';
      const report = `EVENT_VERIFY: ${eventVerifyForm.verified ? 'VERIFIED' : 'ISSUES_FOUND'} - ${eventVerifyForm.details}`;
      await api.post('/security', {
        eventId: eventVerifyForm.eventId,
        report: report,
        verificationType: 'Event Verification',
        approvalStatus: approvalStatus,
        eventVerified: eventVerifyForm.verified,
        eventVerificationDetails: eventVerifyForm.details
      });
      alert('Event verification submitted successfully');
      setShowEventVerifyModal(false);
      setEventVerifyForm({ eventId: '', verified: false, details: '' });
      fetchSecurityLogs();
    } catch (err) {
      alert('Failed to submit: ' + (err?.response?.data?.message || err?.message));
    }
  }

  async function handleSecurityArrangement(e) {
    e.preventDefault();
    try {
      const approvalStatus = securityArrangementForm.verified ? 'Approved' : 'Rejected';
      const report = `SECURITY_ARRANGEMENT: Personnel=${securityArrangementForm.securityPersonnel}, Equipment=${securityArrangementForm.equipment}, Verified=${securityArrangementForm.verified} - ${securityArrangementForm.remarks}`;
      await api.post('/security', {
        eventId: securityArrangementForm.eventId,
        report: report,
        verificationType: 'Security Arrangement',
        approvalStatus: approvalStatus,
        securityPersonnel: securityArrangementForm.securityPersonnel,
        securityEquipment: securityArrangementForm.equipment,
        arrangementVerified: securityArrangementForm.verified,
        arrangementRemarks: securityArrangementForm.remarks
      });
      alert('Security arrangement verification submitted successfully');
      setShowSecurityArrangementModal(false);
      setSecurityArrangementForm({ eventId: '', securityPersonnel: '', equipment: '', verified: false, remarks: '' });
      fetchSecurityLogs();
    } catch (err) {
      alert('Failed to submit: ' + (err?.response?.data?.message || err?.message));
    }
  }

  // Handle event security form submission
  async function handleEventSecuritySubmit(e) {
    e.preventDefault();
    if (!selectedEventForSecurity) return;
    
    try {
      await api.post('/security', {
        eventId: selectedEventForSecurity.id,
        report: eventSecurityForm.report,
        verificationType: 'Security Arrangement',
        approvalStatus: 'Pending',
        securityPersonnel: eventSecurityForm.securityMembers
      });
      alert('Security log created successfully!');
      setShowEventSecurityModal(false);
      setEventSecurityForm({ securityMembers: '', report: '' });
      setSelectedEventForSecurity(null);
      fetchSecurityLogs();
    } catch (err) {
      alert('Failed to create security log: ' + (err?.response?.data?.message || err?.message));
    }
  }

  function handleAddLostItem(e) {
    e.preventDefault();
    const newItem = {
      id: `LF-${Date.now()}`,
      item: lostItemForm.item,
      description: lostItemForm.description,
      location: lostItemForm.location,
      status: lostItemForm.status,
      date: new Date().toLocaleDateString()
    };
    setLostAndFound([...lostAndFound, newItem]);
    setShowLostItemModal(false);
    setLostItemForm({ item: '', description: '', location: '', status: 'Found' });
  }

  function handleUpdateLostItemStatus(id, newStatus) {
    setLostAndFound(lostAndFound.map(item => 
      item.id === id ? { ...item, status: newStatus } : item
    ));
  }

  function handleDeleteLostItem(id) {
    if (window.confirm('Are you sure you want to delete this item?')) {
      setLostAndFound(lostAndFound.filter(item => item.id !== id));
    }
  }

  const pendingVerifications = events.filter(e => e.status === 'Pending' || e.status === 'Approved').length;
  const activeSecurityLogs = securityLogs.filter(log => log.status === 'Open' || log.status === 'In Progress').length;

  // Filter and sort security logs
  const filteredAndSortedLogs = securityLogs
    .filter(log => {
      // Status filter
      if (statusFilter !== 'All' && log.status !== statusFilter) {
        return false;
      }
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          (log.event_name && log.event_name.toLowerCase().includes(query)) ||
          (log.officer_name && log.officer_name.toLowerCase().includes(query)) ||
          (log.report && log.report.toLowerCase().includes(query)) ||
          (log.id && log.id.toString().includes(query))
        );
      }
      return true;
    })
    .sort((a, b) => {
      let aVal, bVal;
      switch (sortBy) {
        case 'id':
          aVal = a.id;
          bVal = b.id;
          break;
        case 'event_name':
          aVal = a.event_name || '';
          bVal = b.event_name || '';
          break;
        case 'officer_name':
          aVal = a.officer_name || '';
          bVal = b.officer_name || '';
          break;
        case 'status':
          aVal = a.status || '';
          bVal = b.status || '';
          break;
        case 'createdAt':
          aVal = new Date(a.createdAt || 0);
          bVal = new Date(b.createdAt || 0);
          break;
        case 'updatedAt':
          aVal = new Date(a.updatedAt || 0);
          bVal = new Date(b.updatedAt || 0);
          break;
        default:
          aVal = a.createdAt;
          bVal = b.createdAt;
      }
      
      if (typeof aVal === 'string') {
        return sortOrder === 'asc' 
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return sortOrder === 'asc' 
        ? aVal - bVal
        : bVal - aVal;
    });

  function handleSort(column) {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  }

  return (
    <div style={{ 
      background: '#EFF6FF',
      minHeight: '100vh',
      width: '100%',
      margin: 0,
      padding: '2rem',
      color: '#1e293b',
      boxSizing: 'border-box'
    }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '2rem',
        paddingBottom: '1.5rem',
        flexWrap: 'wrap',
        gap: '1.5rem',
        borderBottom: '2px solid #e5e7eb',
        backgroundColor: '#ffffff',
        padding: '1.5rem 2rem',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
      }}>
        <div>
          <h1 style={{ 
            fontFamily: 'Poppins, sans-serif',
            fontSize: '2.5rem', 
            fontWeight: 700,
            marginBottom: '0.5rem',
            color: '#1e293b',
            margin: 0
          }}>Security Dashboard</h1>
          <p style={{ 
            color: '#64748b',
            fontSize: '1rem',
            margin: 0,
            marginTop: '0.5rem'
          }}>Manage event security verifications and lost items</p>
        </div>
        <button
          onClick={() => setShowLostItemModal(true)}
          style={{
            padding: '0.75rem 1.5rem',
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '0.875rem',
            whiteSpace: 'nowrap'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.35)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.25)';
          }}
        >
          <FaPlus /> Report Lost Item
        </button>
      </div>

      {error && <div style={{
        marginBottom: '1rem',
        marginLeft: '2rem',
        marginRight: '2rem',
        padding: '1rem',
        background: 'rgba(239, 68, 68, 0.2)',
        border: '1px solid rgba(239, 68, 68, 0.4)',
        borderRadius: '12px',
        color: '#fca5a5'
      }}>{error}</div>}

      {/* Stats Cards */}
      <div className="stats-grid mb-6" style={{ padding: '0 2rem' }}>
        <div style={{
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          display: 'flex',
          alignItems: 'center',
          gap: '1.5rem',
          padding: '1.75rem',
          borderRadius: '16px',
          color: 'white',
          boxShadow: '0 10px 30px rgba(245, 158, 11, 0.3)',
          transition: 'all 0.3s ease',
          cursor: 'pointer'
        }} 
        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-6px)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ fontSize: '3rem', opacity: 0.9 }}>
            <FaCalendarCheck />
          </div>
          <div>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 600, margin: '0 0 0.5rem 0', opacity: 0.95 }}>Pending Verifications</h3>
            <p style={{ fontSize: '2.5rem', fontWeight: 700, margin: 0 }}>{pendingVerifications}</p>
          </div>
        </div>
        
        <div style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
          display: 'flex',
          alignItems: 'center',
          gap: '1.5rem',
          padding: '1.75rem',
          borderRadius: '16px',
          color: 'white',
          boxShadow: '0 10px 30px rgba(59, 130, 246, 0.3)',
          transition: 'all 0.3s ease',
          cursor: 'pointer'
        }}
        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-6px)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ fontSize: '3rem', opacity: 0.9 }}>
            <FaClipboardList />
          </div>
          <div>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 600, margin: '0 0 0.5rem 0', opacity: 0.95 }}>Security Logs</h3>
            <p style={{ fontSize: '2.5rem', fontWeight: 700, margin: 0 }}>{activeSecurityLogs}</p>
          </div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          display: 'flex',
          alignItems: 'center',
          gap: '1.5rem',
          padding: '1.75rem',
          borderRadius: '16px',
          color: 'white',
          boxShadow: '0 10px 30px rgba(16, 185, 129, 0.3)',
          transition: 'all 0.3s ease',
          cursor: 'pointer'
        }}
        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-6px)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ fontSize: '3rem', opacity: 0.9 }}>
            <FaBoxOpen />
          </div>
          <div>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 600, margin: '0 0 0.5rem 0', opacity: 0.95 }}>Lost & Found</h3>
            <p style={{ fontSize: '2.5rem', fontWeight: 700, margin: 0 }}>{lostAndFound.filter(i => i.status === 'Found').length}</p>
          </div>
        </div>
      </div>

      {/* Events Section */}
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        padding: '2rem',
        marginBottom: '1.5rem',
        border: '1px solid #e5e7eb',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
      }}>
          <h3 style={{
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 700,
            fontSize: '1.5rem',
            color: '#1e293b',
            marginTop: 0,
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <span style={{ fontSize: '1.75rem' }}>📅</span> Events
          </h3>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'rgba(241, 245, 249, 0.6)' }}>Loading events...</div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#fca5a5' }}>{error}</div>
        ) : events.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'rgba(241, 245, 249, 0.6)' }}>No events found</div>
        ) : (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '1rem'
          }} className="events-grid">
            {events.map((event) => (
              <div
                key={event.id}
                onClick={() => {
                  setSelectedEventForSecurity(event);
                  setEventSecurityForm({ securityMembers: '', report: '' });
                  setShowEventSecurityModal(true);
                }}
                style={{
                  padding: '1.5rem',
                  background: '#f8fafc',
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f1f5f9';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.12)';
                  e.currentTarget.style.borderColor = '#cbd5e1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f8fafc';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }}
              >
                <div style={{ marginBottom: '1rem' }}>
                  <h4 style={{ 
                    fontWeight: 700, 
                    fontSize: '1.125rem', 
                    color: '#1e293b', 
                    marginBottom: '0.75rem',
                    lineHeight: '1.4'
                  }}>{event.name}</h4>
                  {event.description && (
                    <p style={{ 
                      fontSize: '0.875rem', 
                      color: '#64748b',
                      lineHeight: '1.5',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>{event.description}</p>
                  )}
                </div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  fontSize: '0.75rem',
                  color: '#94a3b8',
                  marginTop: '1rem',
                  paddingTop: '1rem',
                  borderTop: '1px solid #e5e7eb'
                }}>
                  <span>📅 {event.date}</span>
                  {event.time && <span>🕐 {event.time}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Security Logs Table */}
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        padding: '2rem',
        marginBottom: '1.5rem',
        border: '1px solid #e5e7eb',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '1.5rem' 
        }}>
          <h3 style={{
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 700,
            fontSize: '1.5rem',
            color: '#1e293b',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <span style={{ fontSize: '1.75rem' }}>🛡️</span> Security Logs
          </h3>
          <button 
            onClick={fetchSecurityLogs} 
            style={{
              padding: '0.5rem 1rem',
              background: 'rgba(59, 130, 246, 0.2)',
              border: '1px solid rgba(59, 130, 246, 0.4)',
              borderRadius: '8px',
              color: '#93c5fd',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontSize: '0.875rem'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(59, 130, 246, 0.3)';
              e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.6)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(59, 130, 246, 0.2)';
              e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.4)';
            }}
          >
            🔄 Refresh
          </button>
        </div>
        
        {/* Filters and Search */}
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '1rem', 
          alignItems: 'center',
          marginBottom: '1.5rem',
          padding: '1rem',
          background: '#f8fafc',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ flex: '1', minWidth: '200px' }}>
            <input
              type="text"
              placeholder="Search by event, officer, report, or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '0.875rem',
                outline: 'none',
                transition: 'all 0.2s ease'
              }}
              onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label style={{ fontSize: '0.875rem', color: '#475569', fontWeight: 500 }}>Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: '0.5rem 0.75rem',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '0.875rem',
                background: '#ffffff',
                color: '#1e293b',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              <option value="All">All</option>
              <option value="Open">Open</option>
              <option value="In Progress">In Progress</option>
              <option value="Closed">Closed</option>
              <option value="Resolved">Resolved</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>Loading security logs...</div>
        ) : filteredAndSortedLogs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
            {securityLogs.length === 0 ? 'No security logs found' : 'No logs match your filters'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse" style={{ minWidth: '800px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e5e7eb' }}>
                  <th 
                    style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: '#475569',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onClick={() => handleSort('id')}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                  >
                    ID {sortBy === 'id' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: '#475569',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onClick={() => handleSort('event_name')}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                  >
                    Event {sortBy === 'event_name' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: '#475569',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onClick={() => handleSort('officer_name')}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                  >
                    Officer {sortBy === 'officer_name' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th style={{
                    padding: '1rem',
                    textAlign: 'left',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#475569',
                    textTransform: 'uppercase',
                    backgroundColor: '#f8fafc'
                  }}>Report</th>
                  <th 
                    style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: '#475569',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      backgroundColor: '#f8fafc'
                    }}
                    onClick={() => handleSort('status')}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                  >
                    Status {sortBy === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: '#475569',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      backgroundColor: '#f8fafc'
                    }}
                    onClick={() => handleSort('createdAt')}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                  >
                    Created At {sortBy === 'createdAt' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: '#475569',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      backgroundColor: '#f8fafc'
                    }}
                    onClick={() => handleSort('updatedAt')}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                  >
                    Updated At {sortBy === 'updatedAt' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th style={{
                    padding: '1rem',
                    textAlign: 'left',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#475569',
                    textTransform: 'uppercase',
                    backgroundColor: '#f8fafc'
                  }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedLogs.map((log) => (
                  <tr 
                    key={log.id} 
                    style={{
                      borderBottom: '1px solid #e5e7eb',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#1e293b' }}>{log.id}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#1e293b' }}>
                      {log.event_name ? (
                        <div>
                          <div style={{ fontWeight: 600, color: '#1e293b' }}>{log.event_name}</div>
                          {log.event_date && (
                            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                              {new Date(log.event_date).toLocaleDateString()}
                              {log.event_time && ` • ${log.event_time}`}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>N/A</span>
                      )}
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#1e293b' }}>
                      {log.officer_name ? (
                        <div>
                          <div style={{ fontWeight: 600, color: '#1e293b' }}>{log.officer_name}</div>
                          {log.officer_email && (
                            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{log.officer_email}</div>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>N/A</span>
                      )}
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#1e293b', maxWidth: '200px' }}>
                      <div style={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }} title={log.report || ''}>
                        {log.report || 'N/A'}
                      </div>
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                      <select
                        value={log.status || 'Open'}
                        onChange={(e) => handleUpdateSecurityLogStatus(log.id, e.target.value)}
                        style={{
                          padding: '0.5rem 0.75rem',
                          borderRadius: '8px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          border: '1px solid',
                          background: log.status === 'Open' ? '#fef3c7' :
                                     log.status === 'Closed' ? '#d1fae5' :
                                     '#f1f5f9',
                          color: log.status === 'Open' ? '#d97706' :
                                 log.status === 'Closed' ? '#059669' :
                                 '#475569',
                          borderColor: log.status === 'Open' ? '#fcd34d' :
                                      log.status === 'Closed' ? '#6ee7b7' :
                                      '#cbd5e1',
                          cursor: 'pointer',
                          outline: 'none'
                        }}
                      >
                        <option value="Open" style={{ background: '#ffffff', color: '#1e293b' }}>Open</option>
                        <option value="Closed" style={{ background: '#ffffff', color: '#1e293b' }}>Closed</option>
                        <option value="In Progress" style={{ background: '#ffffff', color: '#1e293b' }}>In Progress</option>
                        <option value="Resolved" style={{ background: '#ffffff', color: '#1e293b' }}>Resolved</option>
                      </select>
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#64748b' }}>
                      {log.createdAt ? (
                        <div>
                          <div>{new Date(log.createdAt).toLocaleDateString()}</div>
                          <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{new Date(log.createdAt).toLocaleTimeString()}</div>
                        </div>
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#64748b' }}>
                      {log.updatedAt ? (
                        <div>
                          <div>{new Date(log.updatedAt).toLocaleDateString()}</div>
                          <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{new Date(log.updatedAt).toLocaleTimeString()}</div>
                        </div>
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                      <button
                        onClick={() => handleViewSecurityLog(log)}
                        style={{
                          padding: '0.5rem 0.75rem',
                          background: 'rgba(59, 130, 246, 0.2)',
                          border: '1px solid rgba(59, 130, 246, 0.4)',
                          borderRadius: '8px',
                          color: '#93c5fd',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          fontSize: '0.875rem',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(59, 130, 246, 0.3)';
                          e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.6)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(59, 130, 246, 0.2)';
                          e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.4)';
                        }}
                        title="View Details"
                      >
                        <FaInfoCircle />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredAndSortedLogs.length > 0 && (
              <div style={{
                marginTop: '1rem',
                fontSize: '0.875rem',
                color: 'rgba(241, 245, 249, 0.6)',
                textAlign: 'center',
                padding: '1rem'
              }}>
                Showing {filteredAndSortedLogs.length} of {securityLogs.length} security log(s)
              </div>
            )}
          </div>
        )}
      </div>

      {/* Lost & Found - Moved to Bottom */}
      <div style={{
        background: '#ffffff',
        borderRadius: '12px',
        padding: '2rem',
        marginBottom: '1.5rem',
        border: '1px solid #e5e7eb',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '1.5rem' 
        }}>
          <h3 style={{
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 700,
            fontSize: '1.5rem',
            color: '#1e293b',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <span style={{ fontSize: '1.75rem' }}>🔍</span> Lost & Found
          </h3>
          <button
            onClick={() => setShowLostItemModal(true)}
            style={{
              padding: '0.5rem 1rem',
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
            }}
          >
            <FaPlus /> Add Item
          </button>
        </div>
        {lostAndFound.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>No lost items recorded</div>
        ) : (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '1rem'
          }} className="security-functions-grid">
            {lostAndFound.map((item) => (
              <div 
                key={item.id} 
                style={{
                  padding: '1.25rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  background: '#f8fafc',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f1f5f9';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.12)';
                  e.currentTarget.style.borderColor = '#cbd5e1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f8fafc';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }}
              >
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'start', 
                  marginBottom: '1rem' 
                }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ 
                      fontWeight: 600, 
                      fontSize: '0.875rem', 
                      color: '#1e293b', 
                      marginBottom: '0.5rem' 
                    }}>{item.item}</p>
                    <p style={{ 
                      fontSize: '0.75rem', 
                      color: '#64748b',
                      marginBottom: '0.375rem'
                    }}>{item.description}</p>
                    <p style={{ 
                      fontSize: '0.75rem', 
                      color: '#64748b',
                      marginBottom: '0.375rem'
                    }}>📍 {item.location}</p>
                    <p style={{ 
                      fontSize: '0.75rem', 
                      color: '#94a3b8' 
                    }}>{item.date}</p>
                  </div>
                  <button
                    onClick={() => handleDeleteLostItem(item.id)}
                    style={{
                      padding: '0.5rem',
                      background: 'rgba(239, 68, 68, 0.2)',
                      border: '1px solid rgba(239, 68, 68, 0.4)',
                      borderRadius: '8px',
                      color: '#fca5a5',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      marginLeft: '0.5rem'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(239, 68, 68, 0.3)';
                      e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.6)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
                      e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.4)';
                    }}
                    title="Delete"
                  >
                    <FaTrash />
                  </button>
                </div>
                <select
                  value={item.status}
                  onChange={(e) => handleUpdateLostItemStatus(item.id, e.target.value)}
                    style={{
                      width: '100%',
                      fontSize: '0.75rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '0.5rem 0.75rem',
                      background: '#ffffff',
                      color: '#1e293b',
                      cursor: 'pointer',
                      outline: 'none'
                    }}
                >
                  <option value="Found" style={{ background: '#ffffff', color: '#1e293b' }}>Found</option>
                  <option value="Claimed" style={{ background: '#ffffff', color: '#1e293b' }}>Claimed</option>
                  <option value="Disposed" style={{ background: '#ffffff', color: '#1e293b' }}>Disposed</option>
                </select>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {/* Date Approval Modal */}
      {showDateApprovalModal && (
        <div className="modal-overlay" onClick={() => setShowDateApprovalModal(false)}>
          <div className="modal-card maintenance-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-body">
              <div className="flex-header mb-4">
                <h3 className="hero-title">Verify Date Approval</h3>
                <button onClick={() => setShowDateApprovalModal(false)} className="btn-icon">
                  <FaTimes />
                </button>
              </div>
              <form onSubmit={handleDateApproval}>
                <div className="form-group">
                  <label>Select Event</label>
                  <select
                    className="form-input"
                    value={dateApprovalForm.eventId}
                    onChange={(e) => setDateApprovalForm({ ...dateApprovalForm, eventId: e.target.value })}
                    required
                  >
                    <option value="">Choose an event</option>
                    {events.map(e => (
                      <option key={e.id} value={e.id}>{e.name} - {e.date}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={dateApprovalForm.dateVerified}
                      onChange={(e) => setDateApprovalForm({ ...dateApprovalForm, dateVerified: e.target.checked })}
                      className="mr-2"
                    />
                    Date Approved
                  </label>
                </div>
                <div className="form-group">
                  <label>Remarks</label>
                  <textarea
                    className="form-input"
                    rows="4"
                    value={dateApprovalForm.remarks}
                    onChange={(e) => setDateApprovalForm({ ...dateApprovalForm, remarks: e.target.value })}
                    placeholder="Add any remarks or concerns..."
                  />
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">Submit Approval</button>
                  <button type="button" className="btn btn-outline" onClick={() => setShowDateApprovalModal(false)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Access Authorization Modal */}
      {showAccessAuthModal && (
        <div className="modal-overlay" onClick={() => setShowAccessAuthModal(false)}>
          <div className="modal-card maintenance-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-body">
              <div className="flex-header mb-4">
                <h3 className="hero-title">Access Authorization</h3>
                <button onClick={() => setShowAccessAuthModal(false)} className="btn-icon">
                  <FaTimes />
                </button>
              </div>
              <form onSubmit={handleAccessAuthorization}>
                <div className="form-group">
                  <label>Select Event</label>
                  <select
                    className="form-input"
                    value={accessAuthForm.eventId}
                    onChange={(e) => setAccessAuthForm({ ...accessAuthForm, eventId: e.target.value })}
                    required
                  >
                    <option value="">Choose an event</option>
                    {events.map(e => (
                      <option key={e.id} value={e.id}>{e.name} - {e.date}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Authorized Persons</label>
                  <input
                    type="text"
                    className="form-input"
                    value={accessAuthForm.authorizedPersons}
                    onChange={(e) => setAccessAuthForm({ ...accessAuthForm, authorizedPersons: e.target.value })}
                    placeholder="Enter names or IDs of authorized personnel"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Access Level</label>
                  <select
                    className="form-input"
                    value={accessAuthForm.accessLevel}
                    onChange={(e) => setAccessAuthForm({ ...accessAuthForm, accessLevel: e.target.value })}
                  >
                    <option value="Standard">Standard</option>
                    <option value="Restricted">Restricted</option>
                    <option value="VIP">VIP</option>
                    <option value="Staff Only">Staff Only</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Remarks</label>
                  <textarea
                    className="form-input"
                    rows="3"
                    value={accessAuthForm.remarks}
                    onChange={(e) => setAccessAuthForm({ ...accessAuthForm, remarks: e.target.value })}
                    placeholder="Additional notes..."
                  />
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">Authorize Access</button>
                  <button type="button" className="btn btn-outline" onClick={() => setShowAccessAuthModal(false)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Event Verification Modal */}
      {showEventVerifyModal && (
        <div className="modal-overlay" onClick={() => setShowEventVerifyModal(false)}>
          <div className="modal-card maintenance-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-body">
              <div className="flex-header mb-4">
                <h3 className="hero-title">Verify Event Details</h3>
                <button onClick={() => setShowEventVerifyModal(false)} className="btn-icon">
                  <FaTimes />
                </button>
              </div>
              {selectedEvent && (
                <div className="mb-4 p-3 bg-gray-50 rounded">
                  <h4 className="font-semibold">{selectedEvent.name}</h4>
                  <p className="text-sm text-gray-600">Date: {selectedEvent.date} • Time: {selectedEvent.time}</p>
                  <p className="text-sm text-gray-500">{selectedEvent.description}</p>
                </div>
              )}
              <form onSubmit={handleEventVerification}>
                <div className="form-group">
                  <label>Select Event</label>
                  <select
                    className="form-input"
                    value={eventVerifyForm.eventId}
                    onChange={(e) => {
                      const event = events.find(ev => ev.id === parseInt(e.target.value));
                      setSelectedEvent(event);
                      setEventVerifyForm({ ...eventVerifyForm, eventId: e.target.value });
                    }}
                    required
                  >
                    <option value="">Choose an event</option>
                    {events.map(e => (
                      <option key={e.id} value={e.id}>{e.name} - {e.date}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={eventVerifyForm.verified}
                      onChange={(e) => setEventVerifyForm({ ...eventVerifyForm, verified: e.target.checked })}
                      className="mr-2"
                    />
                    Event Details Verified
                  </label>
                </div>
                <div className="form-group">
                  <label>Verification Details</label>
                  <textarea
                    className="form-input"
                    rows="5"
                    value={eventVerifyForm.details}
                    onChange={(e) => setEventVerifyForm({ ...eventVerifyForm, details: e.target.value })}
                    placeholder="Enter verification details, any issues found, or confirmations..."
                    required
                  />
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">Submit Verification</button>
                  <button type="button" className="btn btn-outline" onClick={() => setShowEventVerifyModal(false)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Security Arrangement Modal */}
      {showSecurityArrangementModal && (
        <div className="modal-overlay" onClick={() => setShowSecurityArrangementModal(false)}>
          <div className="modal-card maintenance-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-body">
              <div className="flex-header mb-4">
                <h3 className="hero-title">Verify Security Arrangements</h3>
                <button onClick={() => setShowSecurityArrangementModal(false)} className="btn-icon">
                  <FaTimes />
                </button>
              </div>
              <form onSubmit={handleSecurityArrangement}>
                <div className="form-group">
                  <label>Select Event</label>
                  <select
                    className="form-input"
                    value={securityArrangementForm.eventId}
                    onChange={(e) => setSecurityArrangementForm({ ...securityArrangementForm, eventId: e.target.value })}
                    required
                  >
                    <option value="">Choose an event</option>
                    {events.map(e => (
                      <option key={e.id} value={e.id}>{e.name} - {e.date}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Security Personnel Assigned</label>
                  <input
                    type="text"
                    className="form-input"
                    value={securityArrangementForm.securityPersonnel}
                    onChange={(e) => setSecurityArrangementForm({ ...securityArrangementForm, securityPersonnel: e.target.value })}
                    placeholder="e.g., 5 officers, 2 supervisors"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Security Equipment</label>
                  <input
                    type="text"
                    className="form-input"
                    value={securityArrangementForm.equipment}
                    onChange={(e) => setSecurityArrangementForm({ ...securityArrangementForm, equipment: e.target.value })}
                    placeholder="e.g., Metal detectors, CCTV, Barriers"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={securityArrangementForm.verified}
                      onChange={(e) => setSecurityArrangementForm({ ...securityArrangementForm, verified: e.target.checked })}
                      className="mr-2"
                    />
                    Arrangements Verified
                  </label>
                </div>
                <div className="form-group">
                  <label>Remarks</label>
                  <textarea
                    className="form-input"
                    rows="4"
                    value={securityArrangementForm.remarks}
                    onChange={(e) => setSecurityArrangementForm({ ...securityArrangementForm, remarks: e.target.value })}
                    placeholder="Additional notes about security arrangements..."
                  />
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">Submit Verification</button>
                  <button type="button" className="btn btn-outline" onClick={() => setShowSecurityArrangementModal(false)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Lost Item Modal */}
      {showLostItemModal && (
        <div className="modal-overlay" onClick={() => setShowLostItemModal(false)}>
          <div className="modal-card maintenance-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-body">
              <div className="flex-header mb-4">
                <h3 className="hero-title">Report Lost Item</h3>
                <button onClick={() => setShowLostItemModal(false)} className="btn-icon">
                  <FaTimes />
                </button>
              </div>
              <form onSubmit={handleAddLostItem}>
                <div className="form-group">
                  <label>Item Name</label>
                  <input
                    type="text"
                    className="form-input"
                    value={lostItemForm.item}
                    onChange={(e) => setLostItemForm({ ...lostItemForm, item: e.target.value })}
                    placeholder="e.g., Black Wallet, iPhone 15"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    className="form-input"
                    rows="3"
                    value={lostItemForm.description}
                    onChange={(e) => setLostItemForm({ ...lostItemForm, description: e.target.value })}
                    placeholder="Describe the item in detail..."
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Location Found</label>
                  <input
                    type="text"
                    className="form-input"
                    value={lostItemForm.location}
                    onChange={(e) => setLostItemForm({ ...lostItemForm, location: e.target.value })}
                    placeholder="e.g., Main Hall, Lobby, Exit 2"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select
                    className="form-input"
                    value={lostItemForm.status}
                    onChange={(e) => setLostItemForm({ ...lostItemForm, status: e.target.value })}
                  >
                    <option value="Found">Found</option>
                    <option value="Claimed">Claimed</option>
                    <option value="Disposed">Disposed</option>
                  </select>
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary">Add Item</button>
                  <button type="button" className="btn btn-outline" onClick={() => setShowLostItemModal(false)}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Event Security Modal */}
      {showEventSecurityModal && selectedEventForSecurity && (
        <div className="modal-overlay" onClick={() => setShowEventSecurityModal(false)}>
          <div className="modal-card maintenance-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-body">
              <div className="flex-header mb-4">
                <h3 className="hero-title">Create Security Log</h3>
                <button onClick={() => setShowEventSecurityModal(false)} className="btn-icon">
                  <FaTimes size={20} />
                </button>
              </div>
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-1">{selectedEventForSecurity.name}</h4>
                {selectedEventForSecurity.description && (
                  <p className="text-sm text-gray-600 mb-2">{selectedEventForSecurity.description}</p>
                )}
                <div className="text-xs text-gray-500">
                  <span>📅 {selectedEventForSecurity.date}</span>
                  {selectedEventForSecurity.time && <span className="ml-3">🕐 {selectedEventForSecurity.time}</span>}
                </div>
              </div>
              <form onSubmit={handleEventSecuritySubmit}>
                <div className="form-group">
                  <label htmlFor="securityMembers">Security Members</label>
                  <input
                    id="securityMembers"
                    type="text"
                    value={eventSecurityForm.securityMembers}
                    onChange={(e) => setEventSecurityForm({ ...eventSecurityForm, securityMembers: e.target.value })}
                    className="form-input"
                    placeholder="Enter security members names (e.g., John Doe, Jane Smith)"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="report">Report</label>
                  <textarea
                    id="report"
                    value={eventSecurityForm.report}
                    onChange={(e) => setEventSecurityForm({ ...eventSecurityForm, report: e.target.value })}
                    className="form-input"
                    rows="5"
                    placeholder="Enter security report details..."
                    required
                  />
                </div>
                <div className="form-actions">
                  <button
                    type="button"
                    onClick={() => setShowEventSecurityModal(false)}
                    className="btn btn-outline"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                  >
                    Submit Security Log
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

